import { Modal, Button as ArcoButton } from '@arco-design/web-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@bay-email/core';
import { useBlock, useFocusIdx } from '@bay-email/editor';
import { Button } from 'react-bootstrap';
import CodemirrorEditor, {
	CodemirrorEditorInstance,
} from '@extensions/components/Form/CodemirrorEditor';
import { MergeTags } from '../attributes/MergeTags';

// Source-level HTML editing for the Raw block and the Text block's "HTML mode".
// Rich-text editing lives in the Tiptap toolbar; this is the only other editor.
export const HtmlEditor: React.FC<{
	visible: boolean;
	setVisible: (v: boolean) => void;
}> = props => {
	const { visible, setVisible } = props;

	const { focusBlock, setValueByIdx } = useBlock();
	const { focusIdx } = useFocusIdx();
	const [content, setContent] = useState(focusBlock?.data.value.content || '');
	const editorRef = useRef<CodemirrorEditorInstance | null>(null);

	useEffect(() => {
		setContent(focusBlock?.data.value.content || '');
	}, [focusBlock?.data.value.content]);

	const onClose = () => {
		setVisible(false);
	};

	const onSave = () => {
		if (!focusBlock) return;

		const nextBlock = {
			...focusBlock,
			data: {
				...focusBlock.data,
				value: {
					...focusBlock.data.value,
					content,
				},
			},
		};

		setValueByIdx(focusIdx, nextBlock);
		onClose();
	};

	const insertMergeTag = useCallback((tagString: string) => {
		if (!tagString) return;
		const editor = editorRef.current;
		if (!editor) {
			setContent(prev => prev + tagString);
			return;
		}
		// replaceSelection goes through onBeforeChange, which updates `content`.
		editor.replaceSelection(tagString);
		editor.focus();
	}, []);

	return (
		<Modal
			style={{ width: '90vw', maxWidth: 'none', top: 20 }}
			title={<div style={{ width: '100%', textAlign: 'left' }}>{t('HTML Editor')}</div>}
			closable={true}
			escToExit={true}
			visible={visible}
			onCancel={onClose}
			footer={
				<div className='d-flex justify-content-between gap-2'>
					<Button
						variant='outline-secondary'
						onClick={onClose}
					>
						{t('Cancel')}
					</Button>
					<Button
						variant='primary'
						onClick={onSave}
					>
						{t('Save')}
					</Button>
				</div>
			}
		>
			{/* Fixed-height shell, same shape as SourceCodePanel's modal editor: the
			    editor host is the flex remainder so CodeMirror's 100% height resolves. */}
			<div
				style={{
					height: '70vh',
					minHeight: 320,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					border: '1px solid var(--color-neutral-3)',
					borderRadius: 'var(--border-radius-small, 2px)',
				}}
			>
				<div
					className='d-flex align-items-center gap-2'
					style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-neutral-3)' }}
				>
					<MergeTags
						value=''
						showSearch
						onChange={insertMergeTag}
					>
						<ArcoButton
							type='outline'
							size='small'
							title={t('Dynamic Fields')}
						>
							{'{ }'} {t('Dynamic Fields')}
						</ArcoButton>
					</MergeTags>
				</div>
				<div style={{ flex: 1, minHeight: 0 }}>
					<CodemirrorEditor
						value={content}
						onChange={setContent}
						refreshKey={visible ? focusIdx : 'closed'}
						mode='xml'
						onEditorMount={editor => {
							editorRef.current = editor;
						}}
					/>
				</div>
			</div>
		</Modal>
	);
};
